import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { type DeviceHullType } from '@/api/devices/types';
import { GetScoutLatestDataResponse, ScoutLatestData, ScoutLatestDataParams } from '@/api/scout';
import { RequestOptions, axios } from '@/common/axios';
import { ExtractFnReturnType, QueryConfig, queryClient } from '@/common/react-query';
import { QUERIES_REFETCH_INTERVAL } from '@/config';
import { isScoutDevice } from '@/utils';

const SCOUT_LATEST_DATA_STALE_TIME_MS = 1000 * 60 * 5;

const scoutLatestDataQueryKey = (spotterId: string | null) => ['scout-latest-data', { spotterId }] as const;

type SpotterIdentity = {
    spotterId: string;
    hullType?: DeviceHullType | null;
};

export type ScoutsLatestDataBySpotterId = Record<string, ScoutLatestData>;

const fetchScoutLatestData = async (
    { spotterId }: ScoutLatestDataParams,
    options?: RequestOptions
): Promise<ScoutLatestData> => {
    const response = await axios.get<unknown, GetScoutLatestDataResponse>(
        `/user-rest/devices/${spotterId}/scout-data`,
        options
    );

    return response.data;
};

/**
 * Each spotter is fetched as a cache entry of its own, which `useScoutLatestData` observes directly and
 * which `fetchQuery` joins rather than duplicates. So this aggregation deliberately hands its own signal
 * to none of them: an entry with no observer of its own is outside react-query's abort accounting, and
 * cancelling one here would fail the request a mounted `useScoutLatestData` had joined. Abandoning this
 * query therefore leaves its per-spotter requests to finish, and they land in the cache for the next
 * caller.
 */
const getScoutsLatestData = async (spotterIds: string[]): Promise<ScoutsLatestDataBySpotterId> => {
    const scoutEntries = await Promise.all(
        spotterIds.map(async (spotterId) => {
            const scoutLatestData = await queryClient.fetchQuery({
                queryKey: scoutLatestDataQueryKey(spotterId),
                queryFn: () => fetchScoutLatestData({ spotterId }),
                staleTime: SCOUT_LATEST_DATA_STALE_TIME_MS,
            });

            return [spotterId, scoutLatestData] as const;
        })
    );

    return Object.fromEntries(scoutEntries);
};

type UseScoutLatestDataOptions = {
    config?: QueryConfig<typeof fetchScoutLatestData>;
} & {
    spotterId?: string | null;
};

type UseScoutsLatestDataOptions = {
    spotterIdentities?: SpotterIdentity[];
    config?: QueryConfig<typeof getScoutsLatestData>;
};

export const useScoutLatestData = ({
    spotterId,
    config: { enabled = true, ...config } = {},
}: UseScoutLatestDataOptions) => {
    return useQuery<ExtractFnReturnType<typeof fetchScoutLatestData>>({
        queryKey: scoutLatestDataQueryKey(spotterId!),
        queryFn: ({ signal }) => fetchScoutLatestData({ spotterId: spotterId! }, { signal }),
        enabled: Boolean(enabled && spotterId),
        staleTime: SCOUT_LATEST_DATA_STALE_TIME_MS,
        refetchInterval: QUERIES_REFETCH_INTERVAL,
        ...config,
    });
};

export const useScoutsLatestData = ({
    spotterIdentities = [],
    config: { enabled = true, ...config } = {},
}: UseScoutsLatestDataOptions) => {
    const scoutSpotterIds = useMemo(
        () =>
            spotterIdentities
                .filter((scout) => Boolean(scout.spotterId) && isScoutDevice(scout.hullType))
                .map(({ spotterId }) => spotterId)
                .sort(),
        [spotterIdentities]
    );

    return useQuery<ExtractFnReturnType<typeof getScoutsLatestData>>({
        queryKey: ['scouts-latest-data', scoutSpotterIds],
        queryFn: () => getScoutsLatestData(scoutSpotterIds),
        enabled: Boolean(enabled && scoutSpotterIds.length > 0),
        staleTime: SCOUT_LATEST_DATA_STALE_TIME_MS,
        refetchInterval: QUERIES_REFETCH_INTERVAL,
        ...config,
    });
};
